import React from "react";
import { Form, Table, Message, Statistic, Segment } from "semantic-ui-react";
import { costearCentros, costoDeLaPerdida } from "../waterBalanceCalc";
import { useWaterBalanceT } from "../useWaterBalanceT";

// Paso 8 — Costeo MFCA (ISO 14051). El motor sigue siendo la única fuente de
// verdad del cálculo (costearCentros/costoDeLaPerdida); acá solo se declaran
// las tarifas. Un centro con tarifa propia la usa; si no, cae a la tarifa
// por defecto del sitio.
//
// El motor (waterBalanceCalc.js) no se tradujo: sigue esperando
// centroId/volumenM3/origen/categoria y tarifas en español (aguaPorM3, etc.).
// Esta traducción es la misma frontera que estado.js#buildEstadoParaMotor.
const tarifasParaMotor = (t) => ({
  aguaPorM3: t?.waterPerM3,
  energiaTermicaPorM3: t?.thermalEnergyPerM3,
  quimicosPorM3: t?.chemicalsPerM3,
  tratamientoPorM3: t?.treatmentPerM3,
});

export function CostosStep({ costs, onChange, centers, outputs, lossClassification }) {
  const t = useWaterBalanceT();
  const tarifas = costs?.tariffs || {};
  const porCentro = costs?.perCenter || [];

  const setTarifaDefecto = (campo, val) => onChange({ ...costs, tariffs: { ...tarifas, [campo]: val } });

  const tarifaDeCentro = (centerId) => porCentro.find((tc) => tc.centerId === centerId) || { centerId };

  const setTarifaDeCentro = (centerId, campo, val) => {
    const existente = porCentro.find((tc) => tc.centerId === centerId);
    const nueva = { ...(existente || { centerId }), [campo]: val };
    onChange({ ...costs, perCenter: existente ? porCentro.map((tc) => (tc.centerId === centerId ? nueva : tc)) : [...porCentro, nueva] });
  };

  const outputsParaMotor = (outputs || []).map((s) => ({ centroId: s.centerId, volumenM3: s.volumeM3, origen: s.origin }));
  const porCentroParaMotor = porCentro.map((tc) => ({ centroId: tc.centerId, ...tarifasParaMotor(tc) }));
  const clasificacionParaMotor = (lossClassification || []).map((c) => ({ categoria: c.category, volumenM3: c.volumeM3 }));

  const resultadoPorCentro = costearCentros(outputsParaMotor, tarifasParaMotor(tarifas), porCentroParaMotor);
  const totalGeneral = resultadoPorCentro.reduce((a, c) => a + c.total, 0);
  const perdida = costoDeLaPerdida(clasificacionParaMotor, tarifasParaMotor(tarifas));

  const campoNumerico = (label, campo, valor, onSet) => (
    <Form.Input
      label={label}
      type="number"
      size="small"
      value={valor?.[campo] ?? ""}
      onChange={(e) => onSet(campo, e.target.value === "" ? "" : Number(e.target.value))}
    />
  );

  return (
    <div className="water-balance-step">
      <h3>{t("water_balance_costing_title")}</h3>
      <Message>{t("water_balance_costing_hint")}</Message>

      <Segment>
        <h5>{t("water_balance_default_tariffs_title")}</h5>
        <Form>
          <Form.Group widths="equal">
            {campoNumerico(t("water_balance_tariff_water"), "waterPerM3", tarifas, setTarifaDefecto)}
            {campoNumerico(t("water_balance_tariff_thermal"), "thermalEnergyPerM3", tarifas, setTarifaDefecto)}
            {campoNumerico(t("water_balance_tariff_chemicals"), "chemicalsPerM3", tarifas, setTarifaDefecto)}
            {campoNumerico(t("water_balance_tariff_treatment"), "treatmentPerM3", tarifas, setTarifaDefecto)}
          </Form.Group>
        </Form>
      </Segment>

      <h5>{t("water_balance_center_tariff_title")}</h5>
      <p style={{ color: "#666", fontSize: 13 }}>{t("water_balance_center_tariff_hint")}</p>
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("water_balance_col_center")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_water_short")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_thermal_short")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_chemicals_short")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_treatment_short")}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(centers || []).map((c) => (
            <Table.Row key={c.id}>
              <Table.Cell>{c.nombre || c.id}</Table.Cell>
              {["waterPerM3", "thermalEnergyPerM3", "chemicalsPerM3", "treatmentPerM3"].map((campo) => (
                <Table.Cell key={campo}>
                  <input
                    type="number"
                    style={{ width: "100%", border: "none" }}
                    placeholder={t("water_balance_tariff_placeholder_site")}
                    value={tarifaDeCentro(c.id)[campo] ?? ""}
                    onChange={(e) => setTarifaDeCentro(c.id, campo, e.target.value === "" ? "" : Number(e.target.value))}
                  />
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <h5 style={{ marginTop: 24 }}>{t("water_balance_cost_by_center_title")}</h5>
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("water_balance_col_center")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">m³</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">{t("water_balance_col_water_short")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">{t("water_balance_col_thermal_energy_header")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">{t("water_balance_col_chemicals_short")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">{t("water_balance_col_treatment_short")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">{t("water_balance_col_total")}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {resultadoPorCentro.map((r) => (
            <Table.Row key={r.centroId}>
              <Table.Cell>{r.centroId}</Table.Cell>
              <Table.Cell textAlign="right">{r.volumenM3.toLocaleString("es-AR")}</Table.Cell>
              <Table.Cell textAlign="right">{r.agua.toLocaleString("es-AR", { maximumFractionDigits: 0 })}</Table.Cell>
              <Table.Cell textAlign="right">{r.energiaTermica.toLocaleString("es-AR", { maximumFractionDigits: 0 })}</Table.Cell>
              <Table.Cell textAlign="right">{r.quimicos.toLocaleString("es-AR", { maximumFractionDigits: 0 })}</Table.Cell>
              <Table.Cell textAlign="right">{r.tratamiento.toLocaleString("es-AR", { maximumFractionDigits: 0 })}</Table.Cell>
              <Table.Cell textAlign="right"><strong>{r.total.toLocaleString("es-AR", { maximumFractionDigits: 0 })}</strong></Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <Statistic.Group widths={2} style={{ marginTop: 16 }}>
        <Statistic size="small">
          <Statistic.Value>{totalGeneral.toLocaleString("es-AR", { maximumFractionDigits: 0 })}</Statistic.Value>
          <Statistic.Label>{t("water_balance_total_period_cost_label")}</Statistic.Label>
        </Statistic>
        <Statistic size="small" color={perdida.total > 0 ? "red" : undefined}>
          <Statistic.Value>{perdida.total.toLocaleString("es-AR", { maximumFractionDigits: 0 })}</Statistic.Value>
          <Statistic.Label>{t("water_balance_real_loss_cost_label", { vol: perdida.volumenM3.toLocaleString("es-AR") })}</Statistic.Label>
        </Statistic>
      </Statistic.Group>
    </div>
  );
}

export default CostosStep;
